import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlaskConical, CheckCircle2, XCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtMoney } from "@/components/Peep";

// Stands in for the PROVIDER-HOSTED payment page while PEEPING PAY is in sandbox.
// Clicking a button only asks the sandbox provider to emit a signed webhook; access is
// granted by the verified webhook handler, never by this page.
export default function SandboxPay() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/pay/checkout/${id}`).then((r) => setS(r.data)).catch(() => setError("Checkout session not found."));
  }, [id]);
  const simulate = async (outcome) => {
    setBusy(true);
    try {
      await api.post(`/pay/sandbox/${id}/simulate`, { outcome });
      navigate(`/app/checkout/return?session=${id}`, { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };
  return (
    <div data-testid="sandbox-pay-screen" className="min-h-screen bg-[#f4f2f7] px-5 py-10 text-[#15121b]">
      <div className="mx-auto max-w-sm">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#6a00ff]"><FlaskConical size={14} /> Sandbox payment provider</p>
        <h1 className="mt-2 text-2xl font-extrabold">Test checkout</h1>
        <p className="mt-1 text-xs text-[#5c5566]">This page represents the processor's hosted payment page. No card is collected and no money moves.</p>
        {s && (
          <div className="mt-6 rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
            <p className="text-sm text-[#5c5566]">{s.plan_name}</p>
            <p data-testid="sandbox-amount" className="mt-1 text-3xl font-extrabold">{fmtMoney(s.amount)}</p>
            {s.status !== "open" ? (
              <p className="mt-4 text-sm">This session is {s.status}.</p>
            ) : (
              <div className="mt-5 flex flex-col gap-2">
                <button disabled={busy} onClick={() => simulate("success")} data-testid="sandbox-approve" className="flex items-center justify-center gap-2 rounded-xl bg-[#15121b] py-3 text-sm font-semibold text-white"><CheckCircle2 size={16} /> Simulate approved payment</button>
                <button disabled={busy} onClick={() => simulate("decline")} data-testid="sandbox-decline" className="flex items-center justify-center gap-2 rounded-xl border border-black/15 py-3 text-sm font-semibold"><XCircle size={16} /> Simulate declined card</button>
              </div>
            )}
          </div>
        )}
        {error && <p data-testid="sandbox-error" className="mt-4 text-sm text-red-600">{error}</p>}
        <button onClick={() => navigate("/app/membership")} className="mt-6 text-xs text-[#5c5566] underline">Cancel and return to PEEPING</button>
      </div>
    </div>
  );
}
